import React from "react";
import Avatar from "boring-avatars";
import {
  AGENT_PALETTE, AGENT_ICON_BY_NAME, AGENT_PALETTE_HEXES,
  resolveAgentVisual, agentInitials,
} from "./agentVisuals";
import { getThemeMode } from "../../../theme/colors";

/**
 * AgentAvatar — 3D-orb identity surface.
 *
 *   ┌────────────────────────────────┐
 *   │  multi-layer halo (tinted)     │
 *   │   ┌──────────────────────┐     │
 *   │   │ orb gradient         │     │  highlight @ top-left
 *   │   │   curved gloss SVG   │     │  base in middle
 *   │   │   white filled glyph │     │  shadow @ bottom-right
 *   │   │   inset rim/light    │     │
 *   │   └──────────────────────┘     │
 *   └────────────────────────────────┘
 *
 * Visual references: 3D character render aesthetic (saturated jewel
 * tones, soft specular highlights, tinted ambient occlusion). Replaces
 * the prior flat-soft-tint look that tested as "outdated and plain".
 *
 * Variants: "tile" (squircle) | "circle" (full pill).
 *
 * Single source of truth for agent identity across:
 *   • iPhone-grid tile (~96px)
 *   • Detail viewer header (~44px) + Identity card preview (~64px)
 *   • Hover preview, command palette, wiring chips (~24-42px)
 */

export default function AgentAvatar({ agent, size = 40, variant = "tile" }) {
  const { color, iconName, source } = resolveAgentVisual(agent);
  const palette = AGENT_PALETTE[color] || AGENT_PALETTE.violet;
  const iconEntry = AGENT_ICON_BY_NAME[iconName];
  const isDark = getThemeMode() === "dark";

  const radius = variant === "circle" ? size / 2 : Math.max(10, size * 0.26);
  const iconSize = Math.round(size * 0.5);
  const { highlight, base, shadow } = palette;

  // 3D-orb gradient: bright spec top-left → base mid → deep shadow.
  const orbBackground =
    `radial-gradient(circle at 30% 22%, ${highlight} 0%, ${base} 38%, ${shadow} 100%)`;

  // Multi-layer halo.
  // Big size = dramatic glow; small = subtle (don't bloom in lists).
  const haloAlpha = size >= 64 ? 0.55 : size >= 40 ? 0.40 : 0.26;
  const haloFar  = `0 0 ${Math.round(size * 0.7)}px ${withAlpha(base, haloAlpha)}`;
  const haloNear = `0 ${Math.round(size * 0.10)}px ${Math.round(size * 0.25)}px ${withAlpha(shadow, 0.55)}`;
  // Inset rim — bright top edge + tinted bottom occlusion to read 3D.
  const insetGlow =
    `inset 0 ${Math.max(1, Math.round(size * 0.06))}px ${Math.round(size * 0.12)}px rgba(255,255,255,0.30), ` +
    `inset 0 ${-Math.max(1, Math.round(size * 0.10))}px ${Math.round(size * 0.20)}px ${withAlpha(shadow, 0.50)}`;
  const boxShadow = `${haloFar}, ${haloNear}, ${insetGlow}`;

  // Marble fallback — same 3D-orb chrome, marble inside instead of icon.
  if (!iconEntry || source === "fallback") {
    return (
      <div
        style={{
          width: size, height: size, borderRadius: radius,
          overflow: "hidden", position: "relative",
          flex: "0 0 auto",
          boxShadow,
        }}
        aria-label={agent?.name || "Agent"}
      >
        <Avatar
          size={size}
          name={agent?.id || agent?.name || "agent"}
          variant="marble"
          colors={AGENT_PALETTE_HEXES.slice(0, 5)}
          square={variant !== "circle"}
        />
        <CurvedGloss radius={radius} />
        <span
          style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 800,
            fontSize: Math.round(size * 0.36),
            letterSpacing: "0.02em",
            textShadow: "0 2px 4px rgba(0,0,0,0.55)",
            pointerEvents: "none",
          }}
        >
          {agentInitials(agent)}
        </span>
      </div>
    );
  }

  // Vibrant 3D orb + curved gloss + white filled glyph.
  const IconCmp = iconEntry.Icon;
  return (
    <div
      style={{
        width: size, height: size, borderRadius: radius,
        position: "relative",
        flex: "0 0 auto",
        background: orbBackground,
        boxShadow,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}
      aria-label={agent?.name || "Agent"}
      title={agent?.name || "Agent"}
    >
      <CurvedGloss radius={radius} />
      <IconCmp
        size={iconSize}
        color="#ffffff"
        strokeWidth={2.4}
        style={{
          position: "relative",
          filter:
            `drop-shadow(0 1.5px 3px ${withAlpha(shadow, 0.65)}) ` +
            `drop-shadow(0 0 6px rgba(255,255,255,0.35))`,
        }}
      />
    </div>
  );
}

// Curved specular highlight — a tilted SVG ellipse that mimics light
// catching a glossy 3D surface. Sits above the orb, below the glyph.
function CurvedGloss({ radius }) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        position: "absolute", inset: 0,
        pointerEvents: "none",
        borderRadius: radius,
      }}
      aria-hidden
    >
      <defs>
        <radialGradient id="agentOrbGloss" cx="0.32" cy="0.22" r="0.55">
          <stop offset="0%"   stopColor="rgba(255,255,255,0.60)" />
          <stop offset="60%"  stopColor="rgba(255,255,255,0.10)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>
      <ellipse
        cx="32" cy="20" rx="42" ry="22"
        fill="url(#agentOrbGloss)"
        transform="rotate(-18 32 20)"
      />
    </svg>
  );
}

// Compose an `#rrggbb` + alpha → `#rrggbbAA` 8-digit hex color string.
function withAlpha(hex, a) {
  if (!hex || !hex.startsWith("#")) return hex;
  const v = Math.max(0, Math.min(1, a));
  const aa = Math.round(v * 255).toString(16).padStart(2, "0");
  // If already 8-digit, replace alpha; otherwise append.
  if (hex.length === 9) return hex.slice(0, 7) + aa;
  return hex + aa;
}
